import XLSX from "xlsx-js-style";
import moment from "moment";
import { formatDate } from "@/utils/utils";
import { eventBus } from "@/main";
import store from "@/store";
import { buildExportStamp } from "@/utils/watermark";

const BRAND_HEX = "2C7BE5";
const BRAND_BORDER_HEX = "1A5FBD";
const ROW_BORDER_HEX = "DEE2E6";
const ZEBRA_BG_HEX = "F8F9FA";
const FOOT_BG_HEX = "E9ECEF";
const FOOT_BORDER_HEX = "ADB5BD";
const TITLE_HEX = "1F2D3D";

const PDF_BRAND_COLOR = `#${BRAND_HEX}`;
const PDF_BRAND_BORDER = `#${BRAND_BORDER_HEX}`;
const PDF_ROW_BORDER = `#${ROW_BORDER_HEX}`;
const PDF_ZEBRA_BG = `#${ZEBRA_BG_HEX}`;
const PDF_FOOT_BG = `#${FOOT_BG_HEX}`;
const PDF_FOOT_BORDER = `#${FOOT_BORDER_HEX}`;

// eslint-disable-next-line no-control-regex
const FILENAME_FORBIDDEN = /[\\/:*?"<>|\x00-\x1f]/g;

function sanitizeForFilename(value) {
  if (!value) return "";
  return String(value).replace(FILENAME_FORBIDDEN, "").trim().replace(/\s+/g, "-");
}

function sanitizeSheetName(value) {
  if (!value) return "Sheet1";
  return String(value).replace(/[\\/?*[\]:]/g, "").slice(0, 31) || "Sheet1";
}

function formatDateForFilename(date) {
  if (!date) return "";
  const m = moment(date);
  return m.isValid() ? m.format("YYYY-MM-DD") : "";
}

function formatDateForHeader(date) {
  if (!date) return "";
  const formatted = formatDate(date);
  return formatted || String(date);
}

export function buildReportFilename({ reportNameEn, doctorName, dateFrom, dateTo, extension = "xlsx" }) {
  const parts = [sanitizeForFilename(reportNameEn) || "Report"];
  const doctor = sanitizeForFilename(doctorName);
  if (doctor) parts.push(doctor);

  const from = formatDateForFilename(dateFrom);
  const to = formatDateForFilename(dateTo);
  if (from && to) {
    parts.push(from === to ? from : `${from}_to_${to}`);
  } else if (from) {
    parts.push(from);
  }

  const base = parts.join("_");
  return extension ? `${base}.${extension}` : base;
}

export function buildHeaderRows({ reportTitle, dateFrom, dateTo, doctorName, branchName, clinicName }) {
  const rows = [];
  if (reportTitle) rows.push([reportTitle]);

  if (dateFrom || dateTo) {
    const from = formatDateForHeader(dateFrom);
    const to = formatDateForHeader(dateTo);
    rows.push([from && to ? `วันที่: ${from} - ${to}` : `วันที่: ${from || to}`]);
  }

  if (doctorName) rows.push([`แพทย์: ${doctorName}`]);

  if (branchName || clinicName) {
    const branchPart = branchName || "";
    const clinicPart = clinicName ? ` (${clinicName})` : "";
    rows.push([`สาขา: ${branchPart}${clinicPart}`]);
  }

  const user = store.getters["moduleUser/getUserInfo"];
  if (user) {
    rows.push([buildExportStamp({ user, now: new Date() })]);
  }

  rows.push([""]);
  return rows;
}

function buildSheetWithHeader(tableEl, headerRows) {
  // `measure` is the column structure that actually lands in the sheet (export-
  // excluded columns/elements removed). Widths & styles must be measured from it
  // — measuring the original would shift every column when a *leading* column is
  // skipped (e.g. the queue's "#" column).
  const measure = tableEl.cloneNode(true);
  measure.querySelectorAll(".sr-only").forEach(el => el.remove());
  measure.querySelectorAll(".report-export-skip").forEach(el => el.remove());

  // Separate clone for the sheet: same columns, plus blank rows reserved for the
  // header block.
  const clone = measure.cloneNode(true);
  for (let i = 0; i < headerRows.length; i++) {
    clone.insertRow(i);
  }

  const ws = XLSX.utils.table_to_sheet(clone);
  XLSX.utils.sheet_add_aoa(ws, headerRows, { origin: "A1" });
  applyExcelColumnWidths(ws, measure);
  applyExcelCellStyles(ws, headerRows.length, measure);
  return ws;
}

const TITLE_STYLE = {
  font: { name: "Tahoma", sz: 14, bold: true, color: { rgb: TITLE_HEX } },
  alignment: { vertical: "center" },
};

const META_STYLE = {
  font: { name: "Tahoma", sz: 11, color: { rgb: TITLE_HEX } },
  alignment: { vertical: "center" },
};

const TABLE_HEADER_STYLE = {
  font: { name: "Tahoma", sz: 11, bold: true, color: { rgb: "FFFFFF" } },
  fill: { patternType: "solid", fgColor: { rgb: BRAND_HEX } },
  alignment: { horizontal: "center", vertical: "center", wrapText: true },
  border: {
    top: { style: "thin", color: { rgb: BRAND_BORDER_HEX } },
    bottom: { style: "thin", color: { rgb: BRAND_BORDER_HEX } },
    left: { style: "thin", color: { rgb: BRAND_BORDER_HEX } },
    right: { style: "thin", color: { rgb: BRAND_BORDER_HEX } },
  },
};

const dataBorder = {
  top: { style: "thin", color: { rgb: ROW_BORDER_HEX } },
  bottom: { style: "thin", color: { rgb: ROW_BORDER_HEX } },
  left: { style: "thin", color: { rgb: ROW_BORDER_HEX } },
  right: { style: "thin", color: { rgb: ROW_BORDER_HEX } },
};

function buildBodyStyle(isEven) {
  const style = {
    font: { name: "Tahoma", sz: 10 },
    alignment: { vertical: "center", wrapText: true },
    border: dataBorder,
  };
  if (isEven) {
    style.fill = { patternType: "solid", fgColor: { rgb: ZEBRA_BG_HEX } };
  }
  return style;
}

const FOOT_STYLE = {
  font: { name: "Tahoma", sz: 10, bold: true },
  fill: { patternType: "solid", fgColor: { rgb: FOOT_BG_HEX } },
  alignment: { vertical: "center" },
  border: {
    top: { style: "medium", color: { rgb: FOOT_BORDER_HEX } },
    bottom: { style: "thin", color: { rgb: ROW_BORDER_HEX } },
    left: { style: "thin", color: { rgb: ROW_BORDER_HEX } },
    right: { style: "thin", color: { rgb: ROW_BORDER_HEX } },
  },
};

function styleCellAt(ws, rowIdx, colIdx, style) {
  const addr = XLSX.utils.encode_cell({ r: rowIdx, c: colIdx });
  if (!ws[addr]) ws[addr] = { t: "s", v: "" };
  ws[addr].s = style;
}

function applyExcelCellStyles(ws, headerOffset, tableEl) {
  const headerCells = Array.from(tableEl.querySelectorAll("thead tr th"));
  const ncols = headerCells.length;
  if (!ncols) return;

  for (let c = 0; c < ncols; c++) {
    if (headerOffset >= 1) styleCellAt(ws, 0, c, c === 0 ? TITLE_STYLE : META_STYLE);
    for (let r = 1; r < headerOffset - 1; r++) {
      styleCellAt(ws, r, c, c === 0 ? META_STYLE : { font: { name: "Tahoma", sz: 11 } });
    }
  }

  const headerRowIdx = headerOffset;
  for (let c = 0; c < ncols; c++) {
    styleCellAt(ws, headerRowIdx, c, TABLE_HEADER_STYLE);
  }

  const bodyRows = Array.from(tableEl.querySelectorAll("tbody tr"));
  bodyRows.forEach((tr, rowIdx) => {
    const cells = Array.from(tr.querySelectorAll("td"));
    cells.forEach((td, colIdx) => {
      if (colIdx >= ncols) return;
      const style = buildBodyStyle(rowIdx % 2 === 1);
      if (td.classList.contains("text-right")) {
        style.alignment = { ...style.alignment, horizontal: "right" };
      } else if (td.classList.contains("text-center")) {
        style.alignment = { ...style.alignment, horizontal: "center" };
      }
      styleCellAt(ws, headerRowIdx + 1 + rowIdx, colIdx, style);
    });
  });

  const footRows = Array.from(tableEl.querySelectorAll("tfoot tr"));
  const bodyCount = bodyRows.length;
  footRows.forEach((tr, footIdx) => {
    const cells = Array.from(tr.querySelectorAll("td, th"));
    cells.forEach((cell, colIdx) => {
      if (colIdx >= ncols) return;
      const style = { ...FOOT_STYLE };
      if (cell.classList.contains("text-right")) {
        style.alignment = { ...FOOT_STYLE.alignment, horizontal: "right" };
      }
      styleCellAt(ws, headerRowIdx + 1 + bodyCount + footIdx, colIdx, style);
    });
  });

  ws["!rows"] = ws["!rows"] || [];
  ws["!rows"][headerRowIdx] = { hpx: 22 };
}

function applyExcelColumnWidths(ws, tableEl) {
  const headerRow = tableEl.querySelector("thead tr:last-child");
  if (!headerRow) return;
  const headers = Array.from(headerRow.querySelectorAll("th"));
  if (!headers.length) return;

  const widths = headers.map(th => (th.textContent || "").trim().length);

  const sampleRows = Array.from(tableEl.querySelectorAll("tbody tr")).slice(0, 50);
  sampleRows.forEach(row => {
    Array.from(row.querySelectorAll("td")).forEach((td, idx) => {
      if (idx >= widths.length) return;
      const len = (td.textContent || "").trim().length;
      if (len > widths[idx]) widths[idx] = len;
    });
  });

  ws["!cols"] = widths.map(w => ({ wch: Math.max(4, Math.min(40, w + 2)) }));
}

// Inner width of a landscape page at 96dpi, minus the 5mm side margins set in
// PdfExport's htmlToPdfOptions. Laying the DOM out at exactly this width means
// html2canvas captures it 1:1 — a 10px font in the DOM prints as a 10px font
// instead of being scaled by however wide the exporter's monitor happened to be.
const PDF_LANDSCAPE_PX = { a4: 1085, a3: 1550 };

const PDF_DEFAULT_LAYOUT = {
  fontSize: "10px",
  headPadding: "6px 8px",
  cellPadding: "4px 8px",
};

// Wide reports can't keep a comfortable font AND stay on A4: past ~15 columns
// the choice is a smaller font, and past ~20 even that isn't enough, so the
// page itself has to grow.
function planPdfLayout(columnCount) {
  if (columnCount > 20) {
    return {
      format: "a3",
      contentWidth: `${PDF_LANDSCAPE_PX.a3}px`,
      fontSize: "10px",
      headPadding: "4px 5px",
      cellPadding: "3px 5px",
    };
  }
  if (columnCount > 15) {
    return {
      format: "a4",
      contentWidth: `${PDF_LANDSCAPE_PX.a4}px`,
      fontSize: "8px",
      headPadding: "3px 3px",
      cellPadding: "2px 3px",
    };
  }
  if (columnCount > 11) {
    return {
      format: "a4",
      contentWidth: `${PDF_LANDSCAPE_PX.a4}px`,
      fontSize: "9px",
      headPadding: "4px 5px",
      cellPadding: "3px 5px",
    };
  }
  return {
    format: "a4",
    contentWidth: `${PDF_LANDSCAPE_PX.a4}px`,
    ...PDF_DEFAULT_LAYOUT,
  };
}

// Widest row in the header — the last thead row is the real column grid, but
// read every row's colspan total and take the max so a report whose grouping
// band is wider than its column row still reports the true count.
function countTableColumns(tableEl) {
  const rows = Array.from(tableEl.querySelectorAll("thead tr"));
  return rows.reduce((max, tr) => {
    const width = Array.from(tr.querySelectorAll("th, td")).reduce(
      (sum, cell) => sum + (cell.colSpan || 1),
      0,
    );
    return Math.max(max, width);
  }, 0);
}

function decoratePdfTable(tableEl, layout = PDF_DEFAULT_LAYOUT) {
  tableEl.style.width = "100%";
  tableEl.style.maxWidth = "100%";
  tableEl.style.borderCollapse = "collapse";
  tableEl.style.fontSize = "10px";
  // Follow the PDF document face (#my-pdf, src/theme/_print-doc.scss)
  tableEl.style.fontFamily = "inherit";

  // Thai has no word spaces, so without an explicit break rule a long
  // treatment name is one unbreakable "word": the table grows past its
  // container and html2canvas clips whatever sits beyond the capture width.
  // Allowing breaks anywhere makes the table wrap instead of overflow.
  const allowWrap = cell => {
    cell.style.overflowWrap = "anywhere";
    cell.style.wordBreak = "break-word";
    // ...except money, which must never break mid-number.
    if (cell.classList.contains("text-right")) cell.style.whiteSpace = "nowrap";
  };

  tableEl.querySelectorAll("thead th").forEach(th => {
    th.style.backgroundColor = PDF_BRAND_COLOR;
    th.style.color = "#fff";
    th.style.fontWeight = "600";
    th.style.padding = layout.headPadding;
    th.style.border = `1px solid ${PDF_BRAND_BORDER}`;
    th.style.textAlign = "center";
    th.style.verticalAlign = "middle";
    allowWrap(th);
  });

  const tbodyRows = Array.from(tableEl.querySelectorAll("tbody tr"));
  tbodyRows.forEach((tr, rowIdx) => {
    const isEven = rowIdx % 2 === 1;
    Array.from(tr.querySelectorAll("td")).forEach(td => {
      td.style.padding = layout.cellPadding;
      td.style.border = `1px solid ${PDF_ROW_BORDER}`;
      td.style.verticalAlign = "middle";
      if (isEven) td.style.backgroundColor = PDF_ZEBRA_BG;
      if (td.classList.contains("text-right")) td.style.textAlign = "right";
      if (td.classList.contains("text-center")) td.style.textAlign = "center";
      allowWrap(td);
    });
  });

  tableEl.querySelectorAll("tfoot td, tfoot th").forEach(cell => {
    cell.style.backgroundColor = PDF_FOOT_BG;
    cell.style.fontWeight = "600";
    cell.style.borderTop = `2px solid ${PDF_FOOT_BORDER}`;
    cell.style.padding = layout.cellPadding;
    cell.style.border = `1px solid ${PDF_ROW_BORDER}`;
    if (cell.classList.contains("text-right")) cell.style.textAlign = "right";
    allowWrap(cell);
  });
}

function resolveTable(input) {
  if (!input) return null;
  if (typeof input === "string") return document.getElementById(input);
  return input;
}

export function exportTableToExcel(options) {
  const {
    tableEl,
    tableId,
    reportTitle,
    reportNameEn,
    dateFrom,
    dateTo,
    doctorName,
    branchName,
    clinicName,
    sheetName,
    sheets,
  } = options;

  document.querySelectorAll(".sr-only").forEach(el => el.remove());

  const wb = XLSX.utils.book_new();

  if (Array.isArray(sheets) && sheets.length > 0) {
    sheets.forEach(sheet => {
      const tbl = resolveTable(sheet.tableEl || sheet.tableId);
      if (!tbl) return;
      const headerRows = buildHeaderRows({
        reportTitle: sheet.reportTitle || reportTitle,
        dateFrom,
        dateTo,
        doctorName: sheet.doctorName || doctorName,
        branchName,
        clinicName,
      });
      const ws = buildSheetWithHeader(tbl, headerRows);
      XLSX.utils.book_append_sheet(wb, ws, sanitizeSheetName(sheet.name));
    });
  } else {
    const tbl = resolveTable(tableEl || tableId);
    if (!tbl) {
      throw new Error("exportTableToExcel: table element not found");
    }
    const headerRows = buildHeaderRows({
      reportTitle,
      dateFrom,
      dateTo,
      doctorName,
      branchName,
      clinicName,
    });
    const ws = buildSheetWithHeader(tbl, headerRows);
    if (sheetName) {
      XLSX.utils.book_append_sheet(wb, ws, sanitizeSheetName(sheetName));
    } else {
      XLSX.utils.book_append_sheet(wb, ws);
    }
  }

  const filename = buildReportFilename({ reportNameEn, doctorName, dateFrom, dateTo });
  XLSX.writeFile(wb, filename);
  return filename;
}

export function buildPdfHeaderHtml({ reportTitle, dateFrom, dateTo, doctorName, branchName, clinicName }) {
  const lines = [];
  if (reportTitle) {
    lines.push(`<h2 style="margin:0 0 8px 0; font-size:18px;">${reportTitle}</h2>`);
  }
  const meta = [];
  if (dateFrom || dateTo) {
    const from = formatDateForHeader(dateFrom);
    const to = formatDateForHeader(dateTo);
    meta.push(from && to ? `วันที่: ${from} - ${to}` : `วันที่: ${from || to}`);
  }
  if (doctorName) meta.push(`แพทย์: ${doctorName}`);
  if (branchName || clinicName) {
    const branchPart = branchName || "";
    const clinicPart = clinicName ? ` (${clinicName})` : "";
    meta.push(`สาขา: ${branchPart}${clinicPart}`);
  }
  if (meta.length) {
    lines.push(
      `<div style="font-size:12px; margin-bottom:12px;">${meta
        .map(m => `<div>${m}</div>`)
        .join("")}</div>`,
    );
  }
  return lines.join("");
}

function buildPdfHeaderEl(meta) {
  const wrapper = document.createElement("div");
  wrapper.innerHTML = buildPdfHeaderHtml(meta);
  return wrapper;
}

function cleanTableClone(rawTable) {
  const clone = rawTable.cloneNode(true);
  clone.querySelectorAll(".sr-only").forEach(el => el.remove());
  clone.querySelectorAll(".report-export-skip").forEach(el => el.remove());
  return clone;
}

function buildSectionLabel(text) {
  const labelEl = document.createElement("h3");
  labelEl.textContent = text;
  labelEl.style.fontSize = "13px";
  labelEl.style.margin = "0 0 6px 0";
  return labelEl;
}

// Returns the assembled body plus the layout its widest table needs — the
// tables are cloned and measured before any of them is decorated, so every
// table in a multi-sheet export ends up on the same page format and font size.
function buildPdfBodyEl({ tableId, tableEl, sheets, autoFit }) {
  const body = document.createElement("div");
  const clones = [];

  if (Array.isArray(sheets) && sheets.length > 0) {
    sheets.forEach((sheet, idx) => {
      const tbl = resolveTable(sheet.tableEl || sheet.tableId);
      if (!tbl) return;
      const section = document.createElement("section");
      if (idx > 0) section.classList.add("page-break-before");
      section.style.marginBottom = "16px";

      if (sheet.name) section.appendChild(buildSectionLabel(sheet.name));

      const clone = cleanTableClone(tbl);
      clones.push(clone);
      section.appendChild(clone);
      body.appendChild(section);
    });
  } else {
    const rawTable = resolveTable(tableEl || tableId);
    if (rawTable) {
      const section = document.createElement("section");
      section.style.marginBottom = "16px";
      const clone = cleanTableClone(rawTable);
      clones.push(clone);
      section.appendChild(clone);
      body.appendChild(section);
    }
  }

  const columnCount = clones.reduce(
    (max, clone) => Math.max(max, countTableColumns(clone)),
    0,
  );
  const layout = autoFit ? planPdfLayout(columnCount) : PDF_DEFAULT_LAYOUT;
  clones.forEach(clone => decoratePdfTable(clone, layout));

  return { body, layout, columnCount };
}

export function exportReportToPdf(options) {
  const {
    tableEl,
    tableId,
    reportTitle,
    reportNameEn,
    dateFrom,
    dateTo,
    doctorName,
    branchName,
    clinicName,
    sheets,
    orientation = "landscape",
    // Opt-in: pick page format, capture width and font size from the table's
    // column count instead of inheriting the exporter's viewport. Reports that
    // don't pass it keep the previous behaviour untouched.
    autoFit = false,
  } = options;

  const headerEl = buildPdfHeaderEl({
    reportTitle,
    dateFrom,
    dateTo,
    doctorName,
    branchName,
    clinicName,
  });

  const { body: bodyEl, layout } = buildPdfBodyEl({
    tableId,
    tableEl,
    sheets,
    autoFit,
  });

  if (!bodyEl.childElementCount) {
    throw new Error("exportReportToPdf: no table element found");
  }

  // vue-html2pdf appends ".pdf" itself, so pass the bare base name to keep
  // PDF and Excel filenames aligned (e.g. Foo_2026-05-01.pdf, Foo_2026-05-01.xlsx).
  const filenameBase = buildReportFilename({
    reportNameEn,
    doctorName,
    dateFrom,
    dateTo,
    extension: "",
  });

  eventBus.$emit("exportReportPdf", {
    header: headerEl,
    body: bodyEl,
    footer: null,
    option: {
      filename: filenameBase,
      orientation,
      ...(autoFit
        ? { format: layout.format, contentWidth: layout.contentWidth }
        : {}),
    },
  });

  return `${filenameBase}.pdf`;
}
